import React, { useState } from "react";
import * as api from "../api";
import { C, R, F, numeric } from "../theme";
import { Icon } from "../icons";
import { Btn, Card, Badge, Chip, Stat, Ref, Callout, EmptyState, SectionHeader, LoyaltyProgress } from "./UI";

function ClientSpace({ setView, setLastOrder }) {
  const [phone, setPhone] = useState("");
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [referral, setReferral] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  // Étape de vérification par SMS (exigée par le serveur quand le SMS est configuré)
  const [otpStep, setOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);

  function formatPhone(v) {
    const digits = v.replace(/\D/g, "");
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return digits.slice(0, 2) + " " + digits.slice(2);
    if (digits.length <= 7) return digits.slice(0, 2) + " " + digits.slice(2, 5) + " " + digits.slice(5);
    return digits.slice(0, 2) + " " + digits.slice(2, 5) + " " + digits.slice(5, 7) + " " + digits.slice(7, 9);
  }

  async function loadClient(cleaned) {
    const data = await api.getClientOrders(cleaned);
    setClientData(data);
    setOtpStep(false); setOtpCode("");
    api.getReferralCode(cleaned).then(r => setReferral(r)).catch(() => {});
  }

  async function handleSearch() {
    const cleaned = phone.replace(/\s/g, "");
    if (cleaned.length < 9) { setError("Veuillez entrer un numéro valide"); return; }
    setLoading(true); setError("");
    try {
      await loadClient(cleaned);
    } catch (err) {
      if (err.data?.otp_required) {
        // Le serveur exige un code SMS : on l'envoie et on affiche l'étape de saisie.
        try {
          await api.requestClientCode(cleaned);
          setOtpStep(true);
        } catch (e2) {
          setError(e2.message || "Impossible d'envoyer le code de vérification.");
        }
      } else {
        setError("Aucun compte trouvé avec ce numéro. Passez une commande pour créer votre espace !");
        setClientData(null);
      }
    } finally { setLoading(false); }
  }

  async function handleConfirmCode() {
    const cleaned = phone.replace(/\s/g, "");
    if (otpCode.trim().length !== 6) { setError("Le code comporte 6 chiffres."); return; }
    setLoading(true); setError("");
    try {
      await api.confirmClientCode(cleaned, otpCode.trim());
      await loadClient(cleaned);
    } catch (err) {
      setError(err.message || "Code incorrect");
    } finally { setLoading(false); }
  }

  async function handleResendCode() {
    const cleaned = phone.replace(/\s/g, "");
    setOtpSending(true); setError("");
    try { await api.requestClientCode(cleaned); }
    catch (e) { setError(e.message || "Impossible de renvoyer le code."); }
    finally { setOtpSending(false); }
  }

  async function shareCode() {
    if (!referral) return;
    const url = `${window.location.origin}?ref=${referral.code}`;
    // Message SORTANT (partage / WhatsApp) : emoji conservé volontairement,
    // c'est du texte envoyé à un proche, pas de l'interface. Cf. utils.js/waMessage().
    const msg = `🎁 Profite de mon code de parrainage *${referral.code}* sur Setal Express pour ta première commande de pressing !\n\n${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Setal Express", text: msg, url }); return; } catch {}
    }
    // Fallback: WhatsApp
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  }

  async function copyCode() {
    if (!referral) return;
    try {
      await navigator.clipboard.writeText(referral.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch {}
  }

  const statusIcon = (s) => {
    const map = {
      "En attente": "pending", "Réceptionné": "package", "Collecté": "collect",
      "En traitement": "washing", "Prêt": "clean", "Livré": "delivery", "Récupéré": "check",
    };
    return map[s] || "list";
  };

  const isActive = (s) => !["Livré", "Récupéré"].includes(s);

  const activeOrders = clientData?.orders?.filter(o => isActive(o.status)) || [];
  const pastOrders = clientData?.orders?.filter(o => !isActive(o.status)) || [];

  // Carte de commande — présentation empilée, adaptée au mobile
  function OrderRow({ o, muted }) {
    return (
      <Card
        hover
        className="animate-fadeInUp"
        style={{ marginBottom: 8, cursor: "pointer", background: muted ? C.surfaceAlt : C.surface }}
        onClick={() => { setLastOrder({ id: o.id }); setView("track"); }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
          <div style={{
            width: 32, height: 32, borderRadius: R.md, flexShrink: 0,
            background: C.navySoft, color: C.navy,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name={statusIcon(o.status)} size={16} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <Ref>{o.id}</Ref>
              <Badge status={o.status} small />
            </div>

            <div style={{ fontSize: F.size.sm, color: C.inkMuted, marginTop: 3, ...numeric }}>
              {new Date(o.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
              {o.items && <span> · {o.items.split(",").filter(Boolean).length} article(s)</span>}
            </div>

            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 8, marginTop: 7,
            }}>
              <span style={{ fontWeight: F.weight.semi, fontSize: F.size.md, color: C.ink, ...numeric }}>
                {Number(o.total_amount || o.price || 0).toLocaleString()} FCFA
              </span>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: F.size.sm, fontWeight: F.weight.medium, color: C.navy,
              }}>
                Suivre <Icon name="chevronRight" size={13} />
              </span>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, paddingBottom: 78 }}>
      {/* ─── En-tête ─────────────────────────────────────────────────────── */}
      <div style={{ background: C.navy, color: C.inkOn, padding: "16px 20px 22px" }}>
        <div style={{ maxWidth: 520, margin: "0 auto" }}>
          <button onClick={() => setView("landing")} className="btn-bounce" style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(255,255,255,.10)", border: "1px solid rgba(255,255,255,.18)",
            color: C.inkOn, borderRadius: R.md, padding: "5px 11px",
            fontSize: F.size.base, fontWeight: F.weight.medium, marginBottom: 12,
          }}>
            <Icon name="back" size={14} />
            Retour à l'accueil
          </button>
          <h1 style={{ fontSize: F.size["2xl"], fontWeight: F.weight.semi, letterSpacing: "-.02em" }}>
            Mon espace client
          </h1>
          <p style={{ fontSize: F.size.base, color: "rgba(255,255,255,.72)", marginTop: 3 }}>
            Retrouvez vos informations et vos commandes
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 520, margin: "0 auto", padding: 20 }}>
        {/* ─── Identification ────────────────────────────────────────────── */}
        {!clientData && !otpStep && (
          <Card className="animate-fadeInUp">
            <SectionHeader
              title="Identifiez-vous"
              subtitle="Entrez votre numéro de téléphone pour accéder à votre espace"
              icon="user"
            />
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ position: "relative", flex: 1 }}>
                <span style={{
                  position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)",
                  fontSize: F.size.md, color: C.inkMuted, pointerEvents: "none", ...numeric,
                }}>
                  +221
                </span>
                <input
                  className="field"
                  type="tel"
                  aria-label="Numéro de téléphone"
                  value={phone}
                  onChange={e => setPhone(formatPhone(e.target.value))}
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                  placeholder="77 123 45 67"
                  style={{ paddingLeft: 50, ...numeric }}
                />
              </div>
              <Btn icon="search" onClick={handleSearch} disabled={loading} loading={loading}>
                Accéder
              </Btn>
            </div>
            {error && <Callout tone="danger" style={{ marginTop: 12 }}>{error}</Callout>}
          </Card>
        )}

        {/* ─── Code de vérification SMS ──────────────────────────────────── */}
        {!clientData && otpStep && (
          <Card className="animate-fadeInUp">
            <SectionHeader
              title="Vérifiez votre numéro"
              subtitle={`Un code à 6 chiffres a été envoyé par SMS au +221 ${formatPhone(phone)}`}
              icon="shield"
            />
            <input
              className="field mono"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              aria-label="Code de vérification à 6 chiffres"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, ""))}
              onKeyDown={e => e.key === "Enter" && handleConfirmCode()}
              placeholder="000000"
              autoFocus
              style={{
                textAlign: "center", fontSize: F.size["2xl"], letterSpacing: ".35em",
                padding: "12px 6px", ...numeric,
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Btn icon="check" onClick={handleConfirmCode} disabled={loading || otpCode.length !== 6}
                loading={loading} style={{ flex: 1 }}>
                Vérifier
              </Btn>
              <Btn variant="ghost" icon="refresh" onClick={handleResendCode}
                disabled={otpSending} loading={otpSending}>
                Renvoyer
              </Btn>
            </div>
            <div style={{ marginTop: 10 }}>
              <Btn variant="subtle" small icon="back"
                onClick={() => { setOtpStep(false); setOtpCode(""); setError(""); }}
                style={{ paddingLeft: 0 }}>
                Changer de numéro
              </Btn>
            </div>
            {error && <Callout tone="danger" style={{ marginTop: 12 }}>{error}</Callout>}
            <Callout tone="neutral" icon="idea" style={{ marginTop: 12 }}>
              Cette vérification protège vos informations : elle garantit que vous êtes bien
              le titulaire de ce numéro. Le code expire dans 5 minutes.
            </Callout>
          </Card>
        )}

        {/* ─── Profil client ─────────────────────────────────────────────── */}
        {clientData && (
          <>
            <Card className="animate-fadeInUp">
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: R.lg, flexShrink: 0,
                  background: C.navy, color: C.inkOn,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: F.size.xl, fontWeight: F.weight.semi,
                }}>
                  {clientData.client.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink }}>
                    {clientData.client.name}
                  </p>
                  <p style={{
                    display: "flex", alignItems: "center", gap: 5, marginTop: 2,
                    fontSize: F.size.base, color: C.inkMuted, ...numeric,
                  }}>
                    <Icon name="mobile" size={13} />
                    +221 {formatPhone(clientData.client.phone)}
                  </p>
                  {clientData.client.address && (
                    <p style={{
                      display: "flex", alignItems: "center", gap: 5, marginTop: 2,
                      fontSize: F.size.base, color: C.inkMuted,
                    }}>
                      <Icon name="location" size={13} />
                      <span className="truncate">{clientData.client.address}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Indicateurs */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                <Stat label="Commandes" icon="order" value={clientData.orders.length} />
                <Stat label="En cours" icon="pending" tone="navy" value={activeOrders.length} />
                <Stat
                  label="Total FCFA"
                  icon="cash"
                  tone="accent"
                  value={clientData.orders.reduce((s, o) => s + Number(o.total_amount || o.price || 0), 0).toLocaleString()}
                />
              </div>

              {/* Programme de fidélité — affiché dès que les données sont disponibles */}
              {clientData.loyalty && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                  <LoyaltyProgress
                    points={clientData.loyalty.points}
                    totalOrders={clientData.orders.length}
                    config={clientData.loyalty.config}
                  />
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <Btn
                  variant="subtle"
                  small
                  icon="refresh"
                  onClick={() => { setClientData(null); setPhone(""); setReferral(null); }}
                  style={{ paddingLeft: 0 }}
                >
                  Changer de compte
                </Btn>
              </div>
            </Card>

            {/* ─── Parrainage — débloqué ─────────────────────────────────── */}
            {referral && referral.unlocked && referral.code && (
              <Card style={{ marginTop: 12 }}>
                <SectionHeader
                  title="Parrainez vos amis"
                  subtitle="Vos amis gagnent une réduction sur leur 1ʳᵉ commande, vous cumulez des points fidélité."
                  icon="handshake"
                />

                <div style={{
                  background: C.amberSoft, border: `1px dashed ${C.amberBorder}`,
                  borderRadius: R.md, padding: "14px 16px", textAlign: "center", marginBottom: 12,
                }}>
                  <div style={{
                    fontSize: F.size.xs, fontWeight: F.weight.semi, letterSpacing: ".08em",
                    textTransform: "uppercase", color: C.inkMuted, marginBottom: 6,
                  }}>
                    Votre code
                  </div>
                  <Ref style={{ fontSize: F.size["2xl"], fontWeight: F.weight.semi, letterSpacing: ".08em" }}>
                    {referral.code}
                  </Ref>
                  {referral.referrals_count > 0 && (
                    <div style={{ marginTop: 9 }}>
                      <Chip tone="success" icon="users">
                        <span style={numeric}>
                          {referral.referrals_count} filleul{referral.referrals_count > 1 ? "s" : ""}
                        </span>
                      </Chip>
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <Btn icon="share" onClick={shareCode} style={{ flex: 1 }}>Partager</Btn>
                  <Btn
                    variant="white"
                    icon={copiedCode ? "check" : "copy"}
                    onClick={copyCode}
                    style={{ flex: 1 }}
                  >
                    {copiedCode ? "Copié" : "Copier"}
                  </Btn>
                </div>
              </Card>
            )}

            {/* ─── Parrainage — verrouillé ───────────────────────────────── */}
            {referral && !referral.unlocked && (
              <Card style={{ marginTop: 12 }} padded={false}>
                <EmptyState
                  compact
                  icon="lock"
                  title="Débloquez votre code de parrainage"
                  description={referral.has_pending_order
                    ? "Votre code sera débloqué dès la livraison de votre 1ʳᵉ commande."
                    : "Passez votre 1ʳᵉ commande pour obtenir un code unique à partager avec vos amis et gagner des points fidélité."}
                  action={!referral.has_pending_order
                    ? <Btn icon="order" onClick={() => setView("order")}>Commander maintenant</Btn>
                    : null}
                />
              </Card>
            )}

            {/* ─── Commandes en cours ────────────────────────────────────── */}
            {activeOrders.length > 0 && (
              <>
                <h2 style={{
                  display: "flex", alignItems: "center", gap: 7,
                  fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink,
                  margin: "20px 0 10px",
                }}>
                  <Icon name="timer" size={16} color={C.inkMuted} />
                  Commandes en cours
                  <span style={{ color: C.inkFaint, fontWeight: F.weight.medium, ...numeric }}>
                    ({activeOrders.length})
                  </span>
                </h2>
                {activeOrders.map(o => <OrderRow key={o.id} o={o} />)}
              </>
            )}

            {/* ─── Commandes terminées ───────────────────────────────────── */}
            {pastOrders.length > 0 && (
              <>
                <h2 style={{
                  display: "flex", alignItems: "center", gap: 7,
                  fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink,
                  margin: "20px 0 10px",
                }}>
                  <Icon name="check" size={16} color={C.inkMuted} />
                  Commandes terminées
                  <span style={{ color: C.inkFaint, fontWeight: F.weight.medium, ...numeric }}>
                    ({pastOrders.length})
                  </span>
                </h2>
                {pastOrders.map(o => <OrderRow key={o.id} o={o} muted />)}
              </>
            )}

            {/* ─── Aucune commande ───────────────────────────────────────── */}
            {clientData.orders.length === 0 && (
              <Card style={{ marginTop: 12 }} padded={false}>
                <EmptyState
                  icon="inbox"
                  title="Aucune commande"
                  description="Vous n'avez pas encore passé de commande."
                  action={<Btn icon="order" onClick={() => setView("order")}>Passer une commande</Btn>}
                />
              </Card>
            )}

            <div style={{ marginTop: 16 }}>
              <Btn fullWidth icon="add" onClick={() => setView("order")}>Nouvelle commande</Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ClientSpace;
